import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { api, hasAccessToken } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useToast } from '../../shell/toast';
import { useFavorites } from '../../shell/favorites-context';
import { useOfferFormat } from '../offers/use-offer-format';
import { reelEmbed } from './reel-embed';
import { attachReelPlayer, type ReelPlayer, type ReelPlayerState } from './reel-player';
import { compactCount } from './reel-counts';
import {
  BrandFacebook, BrandInstagram, BrandMessenger, BrandTelegram, BrandWhatsApp, BrandX,
  IconBookmark, IconChevronDown, IconClose, IconEye, IconEyeOff, IconForward, IconHeart, IconLink, IconMail, IconMore, IconPlay, IconRewind,
  IconShare, IconSoundOff, IconSoundOn, IconStore, IconTag, IconYoutube,
} from './reel-icons';

export type ReelCard = components['schemas']['ReelCard'];
type ReelCounts = ReelCard['counts'];
type ReelLikeState = components['schemas']['ReelLikeState'];
type ReelLikedList = components['schemas']['ReelLikedList'];
type ReelShareCount = components['schemas']['ReelShareCount'];
type ReelViewCount = components['schemas']['ReelViewCount'];

const SEEK_STEP = 10;
const HOLD_FOR_2X_MS = 1000;
const SWIPE_PX = 60;
// The tap zones are invisible layers over the reel: a click must not leave them focused, or the
// next key press draws a focus ring around a third of the video. Keyboard users have space/k,
// j/l and the arrows instead.
const noFocus = (e: React.MouseEvent) => e.preventDefault();
/** YouTube paints its own title bar for the first seconds of a play and on pause; our mask covers it then. */
const YT_TITLE_SECONDS = 3.5;
const MAX_LIKE_IDS = 24;
/** How long YouTube keeps its overlay up after a seek. */
const YT_CHROME_AFTER_SEEK_MS = 2500;
/** A reel counts as viewed once it has been on screen this long (API-261) — once per reel per open. */
const VIEW_AFTER_MS = 2000;

interface ShareTarget {
  key: string;
  name: string;
  bg: string;
  icon: ReactNode;
  href?: string;
  onPick?: () => void;
}

// The fullscreen vertical viewer (design "Reel viewer — hide button, final", FR-C29 v2.10): one
// 9:16 stage (edge to edge on a phone), the reel playing from YouTube's privacy player, a live
// progress line, the business + creator credit with a counter and the sound toggle, a right rail
// (plays · like · save · share with counts · YouTube), the brand-teal hide button fixed under the rail,
// the gradient card with the offer chip and the two CTAs, a nudging next chevron and a share
// sheet (brand apps, the phone's own sheet, copy link — the /r/{id} link).
//
// Playback is Shorts-like through reel-player.ts (no script loaded): tap the centre to pause —
// the very first tap on a muted reel turns the sound on instead, as the browser started it
// muted — the sides for ±10 s, hold the right side for 2×, press or drag the line to scrub. While YouTube
// shows its own title bar (the first seconds and on pause) a mask sits over it so the header
// reads cleanly. Like is the platform's own (sign-in; YouTube's like stays on YouTube), Save is
// the listing's favourite (FR-C6), and the share sheet counts a share (API-260).
export function ReelViewer({ reels, start, onClose, returnFocus }: { reels: ReelCard[]; start: number; onClose: () => void; returnFocus?: HTMLElement | null }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const favorites = useFavorites();
  const fmt = useOfferFormat();
  const [index, setIndex] = useState(Math.min(Math.max(start, 0), reels.length - 1));
  const [sound, setSound] = useState(false);
  const [clean, setClean] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [paused, setPaused] = useState(false);
  const [fast, setFast] = useState(false);
  const [flash, setFlash] = useState<{ side: 'l' | 'r'; n: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [ps, setPs] = useState<ReelPlayerState>({ time: 0, duration: 0, playing: false });
  const [counts, setCounts] = useState<Record<string, ReelCounts>>(() => Object.fromEntries(reels.map((r) => [r.id, { ...r.counts }])));
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const closeRef = useRef<HTMLButtonElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<ReelPlayer | null>(null);
  const touchY = useRef<number | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didHold = useRef(false);
  const soundTouched = useRef(false);
  const soundRef = useRef(sound);
  soundRef.current = sound;
  // The player's address is fixed per reel: sound and seeks go to the running player as commands,
  // so the frame never reloads mid-reel (a reload restarted the reel and brought back YouTube's
  // title bar). A new reel starts muted or not as the viewer is at that moment.
  const src = useRef<{ id: string; url: string } | null>(null);
  const timeRef = useRef(0);
  // A drag on the progress line: where the finger is (0–1) until it lets go.
  const [drag, setDrag] = useState<number | null>(null);
  const viewed = useRef<Set<string>>(new Set());
  // YouTube flashes its own title bar and controls over the player after a seek; the top mask
  // covers that window too (see showMask).
  const [ytChrome, setYtChrome] = useState(false);
  const ytChromeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coverYoutubeChrome = () => {
    setYtChrome(true);
    if (ytChromeTimer.current) clearTimeout(ytChromeTimer.current);
    ytChromeTimer.current = setTimeout(() => setYtChrome(false), YT_CHROME_AFTER_SEEK_MS);
  };
  const seekBy = (side: 'l' | 'r') => {
    playerRef.current?.seekBy(side === 'l' ? -SEEK_STEP : SEEK_STEP);
    setFlash((f) => ({ side, n: (f?.n ?? 0) + 1 }));
    coverYoutubeChrome();
  };
  const reel = reels[index];
  const embed = reel ? reelEmbed(reel.video_id) : null;

  const go = (delta: number) =>
    setIndex((i) => {
      return Math.min(Math.max(i + delta, 0), reels.length - 1);
    });

  useEffect(() => {
    closeRef.current?.focus();
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSheet((open) => {
          if (!open) onClose();
          return false;
        });
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') go(-1);
      // YouTube's own keys for ±10 s.
      else if (e.key === 'j' || e.key === 'l') seekBy(e.key === 'j' ? 'l' : 'r');
      else if (e.key === ' ' || e.key === 'k') {
        if ((e.target as HTMLElement | null)?.tagName === 'BUTTON') return;
        e.preventDefault();
        togglePause();
      }
    };
    document.addEventListener('keydown', onKey);
    // The hearts start in the right state: one read for the reels this viewer holds (API-259).
    if (hasAccessToken()) {
      const ids = reels.slice(0, MAX_LIKE_IDS).map((r) => r.id);
      (api.GET('/me/reel-likes', { params: { query: { ids: ids.join(',') } } }) as Promise<ReelLikedList>)
        .then((res) => setLiked(new Set(res.items)))
        .catch(() => {
          /* best-effort: hearts start empty */
        });
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = prev;
      returnFocus?.focus();
    };
    // Mounted once per open: the listeners read state through updaters and refs, so nothing here goes stale.
  }, []);

  // One remote per mounted player (a new reel, or the sound re-mount): it reports time/state
  // and takes the controls' commands.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    setPs({ time: 0, duration: 0, playing: false });
    setPaused(false);
    setFast(false);
    const player = attachReelPlayer(frame, (s) => {
      timeRef.current = s.time;
      setPs(s);
    });
    playerRef.current = player;
    if (soundRef.current) player.unmute();
    return () => {
      player.dispose();
      playerRef.current = null;
    };
  }, [reel?.id]);

  // Sound is a command to the running player — the reel keeps its place.
  useEffect(() => {
    if (sound) playerRef.current?.unmute();
    else playerRef.current?.mute();
  }, [sound]);

  // The play count (API-261): a reel that stays on screen a couple of seconds is a view —
  // once per reel per open, so swiping back and forth does not inflate it.
  useEffect(() => {
    const id = reel?.id;
    if (!id || viewed.current.has(id)) return;
    const timer = setTimeout(() => {
      viewed.current.add(id);
      setCounts((all) => ({ ...all, [id]: { ...(all[id] ?? { views: 0, likes: 0, saves: 0, shares: 0 }), views: (all[id]?.views ?? 0) + 1 } }));
      (api.POST('/reels/{id}/view', { params: { path: { id } } }) as Promise<ReelViewCount>)
        .then((res) => setCounts((all) => ({ ...all, [id]: { ...all[id]!, views: res.views } })))
        .catch(() => {
          /* a lost beacon only costs the tally */
        });
    }, VIEW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [reel?.id]);

  if (!reel || !embed) return null;
  const bizHref = `/b/${reel.business.slug}`;
  const shareUrl = `${window.location.origin}/r/${reel.id}`;
  const shareText = `${reel.business.name}${reel.caption ? ` — ${reel.caption}` : ''}`;
  const saved = favorites.isSaved(reel.business.id);
  const isLiked = liked.has(reel.id);
  const c = counts[reel.id] ?? reel.counts;
  const total = reels.length;
  const pct = drag !== null ? drag * 100 : ps.duration > 0 ? Math.min(100, (ps.time / ps.duration) * 100) : 0;
  // YouTube paints its title bar (and centre controls) whenever its player is not playing —
  // loading, buffering after a seek, paused — and for a few seconds after it starts or resumes.
  const showMask = paused || ytChrome || !ps.playing || ps.time < YT_TITLE_SECONDS;
  if (src.current?.id !== reel.id) {
    src.current = { id: reel.id, url: embed.embed({ muted: !sound, origin: window.location.origin }) };
  }
  const n = (v: number) => compactCount(lang, v, t);

  const bump = (id: string, key: keyof ReelCounts, by: number, exact?: number) =>
    setCounts((all) => {
      const cur = all[id] ?? { views: 0, likes: 0, saves: 0, shares: 0 };
      return { ...all, [id]: { ...cur, [key]: exact ?? Math.max(0, cur[key] + by) } };
    });

  function togglePause() {
    setPaused((p) => {
      if (p) {
        playerRef.current?.play();
        coverYoutubeChrome();
      } else playerRef.current?.pause();
      return !p;
    });
  }
  const toggleSound = () => {
    soundTouched.current = true;
    setSound((s) => !s);
  };
  const tapCentre = () => {
    // The browser started the reel muted; the first tap is the gesture that turns sound on.
    if (!sound && !soundTouched.current) toggleSound();
    else togglePause();
  };
  const seek = seekBy;
  const endHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
    if (fast) {
      playerRef.current?.setRate(1);
      setFast(false);
    }
  };
  const startHold = () => {
    didHold.current = false;
    holdTimer.current = setTimeout(() => {
      didHold.current = true;
      playerRef.current?.setRate(2);
      setFast(true);
    }, HOLD_FOR_2X_MS);
  };
  // Scrub: press on the line and drag; the line follows the finger and the reel jumps once on
  // release (one seek, not one per pixel — each seek makes YouTube buffer).
  const lineFraction = (e: React.PointerEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return r.width > 0 ? Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) : null;
  };
  const scrubStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    const f = lineFraction(e);
    if (f === null) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag(f);
  };
  const scrubMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (drag === null) return;
    const f = lineFraction(e);
    if (f !== null) setDrag(f);
  };
  const scrubEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (drag === null) return;
    const f = lineFraction(e) ?? drag;
    setDrag(null);
    playerRef.current?.seekTo(f);
    coverYoutubeChrome();
  };
  const like = () => {
    if (!hasAccessToken()) {
      toast.show(t('reels.like_signin'));
      navigate(`/login?next=${encodeURIComponent(shareUrl)}`);
      return;
    }
    const id = reel.id;
    const was = liked.has(id);
    const flip = (on: boolean) =>
      setLiked((s) => {
        const next = new Set(s);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      });
    flip(!was);
    bump(id, 'likes', was ? -1 : 1);
    toast.show(was ? t('reels.unliked_toast') : t('reels.liked_toast'));
    const req = was
      ? api.DELETE('/reels/{id}/like', { params: { path: { id } } })
      : api.PUT('/reels/{id}/like', { params: { path: { id } } });
    (req as Promise<ReelLikeState>)
      .then((res) => {
        flip(res.liked);
        bump(id, 'likes', 0, res.likes);
      })
      .catch(() => {
        // The server said no (signed out meanwhile, reel gone): never show a like it did not take.
        flip(was);
        bump(id, 'likes', was ? 1 : -1);
      });
  };
  const save = () => {
    favorites.toggle(reel.business.id);
    bump(reel.id, 'saves', saved ? -1 : 1);
    toast.show(saved ? t('reels.unsaved_toast') : t('reels.saved_toast'));
  };
  const countShare = () => {
    const id = reel.id;
    bump(id, 'shares', 1);
    (api.POST('/reels/{id}/share', { params: { path: { id } } }) as Promise<ReelShareCount>)
      .then((res) => bump(id, 'shares', 0, res.shares))
      .catch(() => {
        /* a lost beacon only costs the tally */
      });
  };
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.show(t('reels.link_copied'));
      countShare();
    } catch {
      toast.show(t('reels.copy_failed'));
    }
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title: shareText, url: shareUrl });
      setSheet(false);
      countShare();
    } catch {
      /* dismissed */
    }
  };

  // The sheet's six slots: the messaging apps people here use, the phone's own sheet where it
  // exists (that is where Instagram lives — it has no web share link), email to fill the rest.
  const u = encodeURIComponent(shareUrl);
  const txt = encodeURIComponent(shareText);
  const coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const canNative = typeof navigator.share === 'function';
  const targets: ShareTarget[] = [
    { key: 'wa', name: 'WhatsApp', bg: '#25d366', icon: <BrandWhatsApp />, href: `https://wa.me/?text=${txt}%20${u}` },
    { key: 'fb', name: 'Facebook', bg: '#1877f2', icon: <BrandFacebook />, href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    ...(coarse ? [{ key: 'ms', name: 'Messenger', bg: '#0084ff', icon: <BrandMessenger />, href: `fb-messenger://share/?link=${u}` }] : []),
    ...(canNative && coarse
      ? [{ key: 'ig', name: 'Instagram', bg: 'linear-gradient(45deg, #f58529, #dd2a7b 55%, #8134af)', icon: <BrandInstagram />, onPick: nativeShare }]
      : []),
    { key: 'tg', name: 'Telegram', bg: '#26a5e4', icon: <BrandTelegram />, href: `https://t.me/share/url?url=${u}&text=${txt}` },
    { key: 'x', name: 'X', bg: '#000', icon: <BrandX size={16} />, href: `https://twitter.com/intent/tweet?url=${u}&text=${txt}` },
    ...(canNative && !coarse ? [{ key: 'more', name: t('reels.more_apps'), bg: '#5b6673', icon: <IconMore />, onPick: nativeShare }] : []),
    { key: 'mail', name: t('reels.email'), bg: '#5b6673', icon: <IconMail />, href: `mailto:?subject=${txt}&body=${u}` },
  ].slice(0, 6);

  return (
    <div className="rv" role="dialog" aria-modal="true" aria-label={t('reels.reel_n', { n: formatNumber(lang, index + 1) })}>
      <button type="button" className="rv__scrim" aria-label={t('reels.close')} onClick={onClose} />
      <div
        className={`rv__phone${clean ? ' is-clean' : ''}${sheet ? ' is-sheet' : ''}`}
        onTouchStart={(e) => (touchY.current = e.touches[0]?.clientY ?? null)}
        onTouchEnd={(e) => {
          const y0 = touchY.current;
          const y1 = e.changedTouches[0]?.clientY;
          touchY.current = null;
          if (y0 === null || y1 === undefined) return;
          if (y0 - y1 > SWIPE_PX) go(1);
          else if (y1 - y0 > SWIPE_PX) go(-1);
        }}
      >
        <img className="rv__poster" src={embed.poster} alt="" aria-hidden="true" loading="lazy" decoding="async" />
        <iframe
          ref={frameRef}
          key={reel.id}
          className="rv__player"
          src={src.current.url}
          title={reel.caption ?? reel.business.name}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
        />
        <span className={`rv__ytmask${showMask ? ' is-on' : ''}`} aria-hidden="true" />
        <span className={`rv__dim${paused ? ' is-on' : ''}`} aria-hidden="true" />
        <div className="rv__tz">
          <button type="button" className="rv__tz-l" tabIndex={-1} onMouseDown={noFocus} aria-label={t('reels.back_10')} onClick={() => seek('l')} />
          <button
            type="button"
            className="rv__tz-c"
            tabIndex={-1}
            onMouseDown={noFocus}
            aria-label={!sound && !soundTouched.current ? t('reels.tap_sound') : paused ? t('reels.play') : t('reels.pause')}
            aria-pressed={paused}
            onClick={tapCentre}
          />
          <button
            type="button"
            className="rv__tz-r"
            tabIndex={-1}
            onMouseDown={noFocus}
            aria-label={t('reels.fwd_10')}
            onPointerDown={startHold}
            onPointerUp={endHold}
            onPointerCancel={endHold}
            onPointerLeave={endHold}
            onClick={() => {
              if (didHold.current) {
                didHold.current = false;
                return;
              }
              seek('r');
            }}
          />
        </div>
        <button
          type="button"
          className={`rv__line${drag !== null ? ' is-drag' : ''}`}
          role="slider"
          aria-label={t('reels.progress')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          onPointerDown={scrubStart}
          onPointerMove={scrubMove}
          onPointerUp={scrubEnd}
          onPointerCancel={() => setDrag(null)}
        >
          <i style={{ width: `${pct}%` }} />
        </button>
        {flash ? (
          <span key={flash.n} className={`rv__flash rv__flash--${flash.side} is-show`} aria-hidden="true" onAnimationEnd={() => setFlash(null)}>
            {flash.side === 'l' ? <IconRewind /> : <IconForward />}
            <i>{t('reels.ten_s')}</i>
          </span>
        ) : null}
        {paused ? (
          <span className="rv__pauseov" aria-hidden="true">
            <IconPlay size={22} />
          </span>
        ) : null}
        {fast ? (
          <span className="rv__fast" aria-hidden="true">
            {t('reels.speed_2x')} <IconForward size={12} />
          </span>
        ) : !sound && !soundTouched.current && !clean ? (
          <span className="rv__fast rv__fast--hint" aria-hidden="true">
            <IconSoundOff size={12} /> {t('reels.tap_sound')}
          </span>
        ) : null}

        <div className="rv__top">
          <Link className="rv__who" to={bizHref} onClick={onClose}>
            <span className="rv__ava" aria-hidden="true">
              {reel.business.name.trim().charAt(0).toUpperCase()}
            </span>
            <b>
              {reel.business.name}
              {reel.creator ? <span className="rv__creator"> · {t('reels.by', { creator: reel.creator })}</span> : null}
            </b>
          </Link>
          <span className="rv__count" aria-label={t('reels.reel_n', { n: formatNumber(lang, index + 1) })}>
            {t('reels.counter', { n: formatNumber(lang, index + 1), total: formatNumber(lang, total) })}
          </span>
          <button type="button" className={`rv__x rv__snd${sound ? ' is-on' : ''}`} aria-pressed={sound} aria-label={sound ? t('reels.sound_on') : t('reels.sound_off')} onClick={toggleSound}>
            {sound ? <IconSoundOn size={13} /> : <IconSoundOff size={13} />}
          </button>
          <button ref={closeRef} type="button" className="rv__x" aria-label={t('reels.close')} onClick={onClose}>
            <IconClose size={13} />
          </button>
        </div>

        <div className="rv__rail">
          <div className="rv__rw">
            <span className="rv__ic rv__ic--stat" role="img" aria-label={t(c.views === 1 ? 'reels.views_one' : 'reels.views', { n: n(c.views) })}>
              <IconPlay size={13} />
            </span>
            <span className="rv__rc" aria-hidden="true">
              {n(c.views)}
            </span>
          </div>
          <div className="rv__rw">
            <button type="button" className={`rv__ic${isLiked ? ' is-on' : ''}`} aria-pressed={isLiked} aria-label={t('reels.like')} onClick={like}>
              <IconHeart size={15} filled={isLiked} />
            </button>
            <span className="rv__rc" aria-hidden="true">
              {n(c.likes)}
            </span>
          </div>
          <div className="rv__rw">
            <button type="button" className={`rv__ic${saved ? ' is-on' : ''}`} aria-pressed={saved} aria-label={t('reels.save')} onClick={save}>
              <IconBookmark size={15} filled={saved} />
            </button>
            <span className="rv__rc" aria-hidden="true">
              {n(c.saves)}
            </span>
          </div>
          <div className="rv__rw">
            <button type="button" className="rv__ic" aria-label={t('reels.share')} aria-expanded={sheet} onClick={() => setSheet(true)}>
              <IconShare size={15} />
            </button>
            <span className="rv__rc" aria-hidden="true">
              {n(c.shares)}
            </span>
          </div>
          <div className="rv__rw">
            <a className="rv__ic" href={embed.watch} target="_blank" rel="noopener noreferrer" aria-label={t('reels.watch_youtube')}>
              <IconYoutube size={15} />
            </a>
          </div>
        </div>
        <button type="button" className="rv__hide" aria-pressed={clean} aria-label={clean ? t('reels.show_ui') : t('reels.hide_ui')} onClick={() => setClean((v) => !v)}>
          {clean ? <IconEye size={15} /> : <IconEyeOff size={15} />}
        </button>
        {index < total - 1 ? (
          <button type="button" className="rv__next" aria-label={t('reels.next')} onClick={() => go(1)}>
            <IconChevronDown size={15} />
          </button>
        ) : null}

        <div className="rv__bot">
          <div className="rv__gradbrd">
            <div className="rv__card">
              {reel.offer ? (
                <span className="rv__ochip">
                  <IconTag size={10} />{' '}
                  {`${reel.offer.discount_kind === 'percent' ? t('dc52.off', { n: fmt.num(reel.offer.discount_value) }) : t('dc52.off_amount', { amount: fmt.money(reel.offer.discount_value) })} · ${fmt.ends(reel.offer).label}`}
                </span>
              ) : null}
              {reel.caption ? <p className="rv__cap">{reel.caption}</p> : null}
              <div className="rv__row">
                {reel.offer_id ? (
                  <Link className="rv__cta rv__cta--hero" to={`${bizHref}#bz-offers`} onClick={onClose}>
                    <IconTag size={12} /> {t('reels.view_offer')}
                  </Link>
                ) : null}
                <Link className={`rv__cta ${reel.offer_id ? 'rv__cta--ring' : 'rv__cta--hero'}`} to={bizHref} onClick={onClose}>
                  <IconStore size={12} /> {reel.offer_id ? t('reels.business') : t('reels.view_business')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button type="button" className="rv__shscrim" tabIndex={sheet ? 0 : -1} aria-label={t('reels.close')} onClick={() => setSheet(false)} />
        <div className="rv__sheet" role="dialog" aria-label={t('reels.share_title')} aria-hidden={!sheet}>
          <span className="rv__shgrab" aria-hidden="true" />
          <div className="rv__shhead">
            <b>{t('reels.share_title')}</b>
            <button type="button" className="rv__shx" tabIndex={sheet ? 0 : -1} aria-label={t('reels.close')} onClick={() => setSheet(false)}>
              <IconClose size={12} />
            </button>
          </div>
          <div className="rv__shgrid">
            {targets.map((p) =>
              p.href ? (
                <a
                  key={p.key}
                  className="rv__pit"
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  tabIndex={sheet ? 0 : -1}
                  aria-label={t('reels.share_via', { platform: p.name })}
                  onClick={() => {
                    setSheet(false);
                    countShare();
                  }}
                >
                  <i style={{ background: p.bg }} aria-hidden="true">
                    {p.icon}
                  </i>
                  {p.name}
                </a>
              ) : (
                <button key={p.key} type="button" className="rv__pit" tabIndex={sheet ? 0 : -1} aria-label={t('reels.share_via', { platform: p.name })} onClick={p.onPick}>
                  <i style={{ background: p.bg }} aria-hidden="true">
                    {p.icon}
                  </i>
                  {p.name}
                </button>
              ),
            )}
          </div>
          <div className="rv__cprow">
            <span>{shareUrl.replace(/^https?:\/\/(www\.)?/, '')}</span>
            <button type="button" className="rv__cpbtn" tabIndex={sheet ? 0 : -1} onClick={copy}>
              <IconLink size={11} /> {copied ? t('reels.copied') : t('reels.copy_link')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
